/**
 * Hand-written wire DTOs + view models for the FHIR Integrations admin feature
 * (PTV2-1145 / PTV2-1126). The backend (`/api/v1/fhir-integrations/*`) is not yet
 * merged, so these are authored from the shared API contract and the verified
 * commons-lib-v2 DTOs rather than generated. Every wire field is optional
 * (`@JsonInclude(NON_NULL)` on the server); the view models normalise to
 * non-null values so the UI does not branch on `undefined`.
 */

// ---- Wire DTOs ----

export interface FhirIdentifierMappingDto {
  readonly guid?: string;
  readonly fhirSystemValue?: string;
  readonly identifierGuid?: string;
  /** Read-only, resolved by the server from the referenced Identifier definition. */
  readonly identifierUserEnteredId?: string;
  /** Read-only, resolved by the server from the referenced Identifier definition. */
  readonly identifierLabel?: string;
  /** Optional display override; falls back to `identifierLabel` when blank. */
  readonly label?: string;
  readonly usedForPatientIdResolution?: boolean;
  readonly active?: boolean;
  readonly indexNumber?: number;
  readonly source?: string;
}

export interface FhirIdentifierMappingRequest {
  readonly fhirSystemValue: string;
  readonly identifierGuid: string;
  readonly label?: string;
  readonly usedForPatientIdResolution?: boolean;
  readonly active?: boolean;
}

export type UpdateFhirIdentifierMappingRequest = Partial<FhirIdentifierMappingRequest>;

export interface FhirMappedFieldsDto {
  readonly fields?: readonly string[];
  /** Read-only catalog of mappable FHIR fields for the multi-select. */
  readonly available?: readonly string[];
  readonly source?: string;
}

export interface UpdateMappedFieldsRequest {
  readonly fields: readonly string[];
}

export interface SystemAccountStatusDto {
  readonly username?: string | null;
  readonly passwordConfigured?: boolean;
  readonly source?: string;
}

export interface UpdateSystemAccountRequest {
  readonly username: string;
  readonly password: string;
}

// ---- View models ----

export interface FhirIdentifierMappingViewModel {
  readonly guid: string;
  readonly fhirSystemValue: string;
  readonly identifierGuid: string;
  readonly identifierUserEnteredId: string;
  readonly identifierLabel: string;
  /** Effective display label: explicit override, else the identifier's own label. */
  readonly label: string;
  readonly usedForPatientIdResolution: boolean;
  readonly active: boolean;
  readonly indexNumber: number | null;
}

export interface MappedFieldsViewModel {
  readonly selected: readonly string[];
  readonly available: readonly string[];
}

export interface SystemAccountViewModel {
  readonly username: string | null;
  readonly passwordConfigured: boolean;
}

// ---- Mappers ----

export function mapFhirIdentifierMapping(
  dto: FhirIdentifierMappingDto,
): FhirIdentifierMappingViewModel {
  const override = dto.label?.trim();
  const identifierLabel = dto.identifierLabel ?? '';
  return {
    guid: dto.guid ?? '',
    fhirSystemValue: dto.fhirSystemValue ?? '',
    identifierGuid: dto.identifierGuid ?? '',
    identifierUserEnteredId: dto.identifierUserEnteredId ?? '',
    identifierLabel,
    label: override !== undefined && override.length > 0 ? override : identifierLabel,
    // BE columns default false/true; the `??` keeps the FE safe if the contract drifts.
    usedForPatientIdResolution: dto.usedForPatientIdResolution ?? false,
    active: dto.active ?? true,
    indexNumber: dto.indexNumber ?? null,
  };
}

export function mapFhirMappedFields(dto: FhirMappedFieldsDto): MappedFieldsViewModel {
  return {
    selected: dto.fields ?? [],
    available: dto.available ?? [],
  };
}

export function mapSystemAccount(dto: SystemAccountStatusDto): SystemAccountViewModel {
  return {
    username: dto.username ?? null,
    passwordConfigured: dto.passwordConfigured ?? false,
  };
}
